import { z } from "zod";

import { API_MANAGER_STATUS } from "../../config/Endpoints.js";
import { getApi } from "../utils/api.ts";

const managerStatusSchema = z.object({
  // The API currently spells this property "leavemanger".
  leavemanger: z.boolean(),
  reportingManager: z.boolean(),
});

export type ManagerStatus = {
  isLeaveManager: boolean;
  isReportingManager: boolean;
};

const USER_DATA_KEY = "userData";
const storedManagerStatusSchema = z.object({
  isLeaveManager: z.boolean(),
  isReportingManager: z.boolean(),
});

function legacyManagerStatusStorageKey(userId: string | number): string {
  return `managerStatus:${encodeURIComponent(String(userId))}`;
}

function readStoredUser(userId: string | number): Record<string, unknown> | null {
  try {
    const value = localStorage.getItem(USER_DATA_KEY);
    if (!value) return null;
    const parsed: unknown = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    const user = parsed as Record<string, unknown>;
    const storedId = user.id ?? user.email;
    return storedId != null && String(storedId) === String(userId) ? user : null;
  } catch {
    return null;
  }
}

export function readManagerStatus(userId: string | number): ManagerStatus | null {
  const user = readStoredUser(userId);
  if (!user) return null;
  const parsed = storedManagerStatusSchema.safeParse(user);
  return parsed.success ? parsed.data : null;
}

export function clearManagerStatus(userId: string | number): void {
  const user = readStoredUser(userId);
  if (user) {
    const nextUser = { ...user };
    delete nextUser.isLeaveManager;
    delete nextUser.isReportingManager;
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(nextUser));
  }
  localStorage.removeItem(legacyManagerStatusStorageKey(userId));
}

export function writeManagerStatus(userId: string | number, status: ManagerStatus): void {
  const user = readStoredUser(userId);
  if (!user) return;
  localStorage.setItem(USER_DATA_KEY, JSON.stringify({ ...user, ...status }));
  localStorage.removeItem(legacyManagerStatusStorageKey(userId));
}

export async function fetchManagerStatus(
  token: string,
  signal?: AbortSignal,
): Promise<ManagerStatus> {
  const { data } = await getApi<unknown>(API_MANAGER_STATUS, token, "json", signal);
  const raw = data != null && typeof data === "object" && "data" in data
    ? (data as { data: unknown }).data
    : data;
  const parsed = managerStatusSchema.parse(raw);
  return {
    isLeaveManager: parsed.leavemanger,
    isReportingManager: parsed.reportingManager,
  };
}
